import { useState, useEffect } from 'react';
import { Icon, IconNames } from '@components/icon';
import { Page } from '@components/page';
import {
  getPaginationUlStylesAsClassName,
  getPaginationLiStylesAsClassName,
} from './pagination-styles';

export interface IPaginationProps {
  /** The total number of pages */
  totalPages: number;
  /** Current page to start */
  currentPage?: number;
  /** Total number of pages the Pagination will show in screen */
  boundaryPagesToShow?: number;
  /** Number of pages to the left and right of the current page */
  siblingCount?: number;
  /** Aria label for the nav element */
  navAriaLabel?: string;
  /** Aria label for all pages, should be something like "Go to page" as the component will add the page number after the text */
  pagesAriaLabel?: string;
  /** Function to be executed when the page changes */
  onPageChange: (page: number) => void;
}

export const Pagination = ({
  totalPages,
  currentPage,
  boundaryPagesToShow = 7,
  siblingCount = 2,
  navAriaLabel = 'Pagination',
  pagesAriaLabel = 'Go to page',
  onPageChange,
}: IPaginationProps): JSX.Element => {
  const dotsAllowed = totalPages - boundaryPagesToShow >= siblingCount;
  const pages = Array.from(Array(totalPages).keys()).map((page) => page + 1);

  const [selectedPage, setSelectedPage] = useState(currentPage || pages[0]);

  const onPageClick = (page: number) => {
    setSelectedPage(page);
    onPageChange(page);
  };

  useEffect(() => {
    if (currentPage) {
      setSelectedPage(currentPage);
    }
  }, [currentPage]);

  /**
   * @param page number
   * @returns boolean
   * Check that the page is one of the first before the dots appear
   */
  const isPageBeforeDots = (page: number): boolean => {
    const firstPagesBeforeDots = boundaryPagesToShow - siblingCount;

    return selectedPage <= firstPagesBeforeDots && page <= boundaryPagesToShow;
  };

  /**
   * @param page number
   * @returns boolean
   * Same as "isPageBeforeDots" but here we check that page is one of the lastests
   * when the dots disappear
   */
  const isPageAfterDots = (page: number): boolean => {
    const lastPages = totalPages - boundaryPagesToShow;
    const lastPagesAfterDots = lastPages + siblingCount;

    return selectedPage > lastPagesAfterDots && page > lastPages;
  };

  const canRenderPage = (page: number): boolean => {
    const isFirstPage = page === 1;
    const isLastPage = page === totalPages;

    const inPagesBefore = page >= selectedPage - siblingCount;
    const inPagesAfter = page <= selectedPage + siblingCount;

    const inMiddlePages =
      inPagesBefore &&
      inPagesAfter &&
      !isPageBeforeDots(selectedPage) &&
      !isPageAfterDots(selectedPage);

    return (
      !isFirstPage &&
      !isLastPage &&
      (isPageBeforeDots(page) || isPageAfterDots(page) || inMiddlePages)
    );
  };

  const pagesToRender = pages.filter(canRenderPage);

  return (
    <nav aria-label={navAriaLabel}>
      <ol className={getPaginationUlStylesAsClassName}>
        <li key="prev-page" className={getPaginationLiStylesAsClassName}>
          <Page
            withIcon
            disabled={selectedPage === 1}
            onClick={() => onPageClick(selectedPage - 1)}
            ariaLabel={
              selectedPage > 1
                ? `${pagesAriaLabel} ${selectedPage - 1}`
                : undefined
            }
          >
            <Icon className="text-primary-main" name={IconNames.CHEVRON_LEFT} />
          </Page>
        </li>

        <li key="page-1" className={getPaginationLiStylesAsClassName}>
          <Page
            onClick={() => onPageClick(1)}
            selected={selectedPage === 1}
            ariaLabel={selectedPage > 1 ? `${pagesAriaLabel} 1` : undefined}
          >
            1
          </Page>
        </li>

        {!isPageBeforeDots(selectedPage) && dotsAllowed && (
          <li key="page-dots-1" className={getPaginationLiStylesAsClassName}>
            <Page selectable={false}>...</Page>
          </li>
        )}

        {pagesToRender.map((page) => (
          <li key={`page-${page}`} className={getPaginationLiStylesAsClassName}>
            <Page
              selected={selectedPage === page}
              onClick={() => onPageClick(page)}
              ariaLabel={
                page !== selectedPage ? `${pagesAriaLabel} ${page}` : undefined
              }
            >
              {page}
            </Page>
          </li>
        ))}

        {!isPageAfterDots(selectedPage) && dotsAllowed && (
          <li key="page-dots-2" className={getPaginationLiStylesAsClassName}>
            <Page selectable={false}>...</Page>
          </li>
        )}

        {totalPages > 1 && (
          <li
            key={`page-${totalPages}`}
            className={getPaginationLiStylesAsClassName}
          >
            <Page
              ariaLabel={`${pagesAriaLabel} ${totalPages}`}
              selected={selectedPage === totalPages}
              onClick={() => onPageClick(totalPages)}
            >
              {totalPages}
            </Page>
          </li>
        )}

        <li key="next-page" className={getPaginationLiStylesAsClassName}>
          <Page
            withIcon
            disabled={totalPages === selectedPage}
            onClick={() => onPageClick(selectedPage + 1)}
            ariaLabel={
              selectedPage < totalPages
                ? `${pagesAriaLabel} ${selectedPage + 1}`
                : undefined
            }
          >
            <Icon
              className="text-primary-main"
              name={IconNames.CHEVRON_RIGHT}
            />
          </Page>
        </li>
      </ol>
    </nav>
  );
};
